import React from 'react';
import { useTranslation } from 'react-i18next';

const OSelect = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select an option',
  name = '',
  id = '',
  register,
  errors = {},
  inputLabel = '',
  disabled = false,
  className = '',
  isRequired = false,
  errorMessage = '',
  showClear = false,
  onClear,
  extraProps = {},
  wrapperClassName = ''
}) => {
  const { t } = useTranslation();
  const selectId = id || name;
  
  // Get error for this field
  const fieldError = errors?.[name];
  
  // Handle clear button click
  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClear) {
      onClear();
    }
    if (onChange) {
      onChange('');
    }
  };
  
  // Check if inputLabel is JSX or string
  const renderLabel = () => {
    if (!inputLabel) return null;
    
    // If it's already a React element (JSX), return it as-is
    if (React.isValidElement(inputLabel)) {
      return inputLabel;
    }
    
    // If it's a string, translate it
    return t(inputLabel) || inputLabel;
  };
  
  // Common styles for the select element
  const selectClassName = `
    block
    w-full
    px-3
    py-2.5
    text-sm
    text-gray-900
    bg-white
    border
    rounded-lg
    appearance-none
    focus:outline-none
    focus:ring-2
    focus:ring-blue-500
    focus:border-blue-500
    dark:bg-gray-700
    dark:border-gray-600
    dark:placeholder-gray-400
    dark:text-white
    dark:focus:ring-blue-500
    dark:focus:border-blue-500
    transition-all
    duration-200
    ease-in-out
    ${fieldError || errorMessage ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}
    ${disabled ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : ''}
    ${showClear && value ? 'pr-10' : ''}
    ${className}
  `;
  
  // If register is provided (for React Hook Form)
  if (register && typeof register === 'function') {
    const registeredField = register(name, {
      required: isRequired ? (t('FIELD_REQUIRED') || 'This field is required') : false
    });
    
    return (
      <div className={`mb-4 ${wrapperClassName}`}>
        {inputLabel && (
          <label
            htmlFor={selectId}
            className={`
              block
              mb-2
              text-sm
              font-medium
              text-gray-900
              dark:text-gray-300
              ${isRequired && !React.isValidElement(inputLabel) ? 'after:content-["*"] after:ml-0.5 after:text-red-500' : ''}
            `}
          >
            {renderLabel()}
          </label>
        )}
        
        <div className="relative">
          <select
            id={selectId}
            {...registeredField}
            className={selectClassName}
            disabled={disabled}
            aria-label={typeof inputLabel === 'string' ? inputLabel : ''}
            aria-invalid={!!fieldError || !!errorMessage}
            aria-describedby={fieldError || errorMessage ? `${selectId}-error` : undefined}
            {...extraProps}
          >
            <option value="" disabled>
              {t(placeholder) || placeholder}
            </option>
            {options.map((option, index) => (
              <option
                key={option.value || `option-${index}`}
                value={option.value}
                disabled={option.disabled}
                className={option.disabled ? 'text-gray-400' : ''}
              >
                {t(option.label) || option.label}
              </option>
            ))}
          </select>
          
          {/* Custom dropdown arrow */}
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
            <svg
              className="fill-current h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
            </svg>
          </div>
          
          {/* Clear button */}
          {showClear && value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute inset-y-0 right-6 flex items-center px-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              title={t('CLEAR') || 'Clear'}
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
        
        {/* Error message */}
        {(fieldError || errorMessage) && (
          <p
            id={`${selectId}-error`}
            className="mt-1 text-sm text-red-600 dark:text-red-400"
          >
            {fieldError?.message || errorMessage}
          </p>
        )}
      </div>
    );
  }
  
  // Without React Hook Form (controlled component)
  return (
    <div className={`mb-4 ${wrapperClassName}`}>
      {inputLabel && (
        <label
          htmlFor={selectId}
          className={`
            block
            mb-2
            text-sm
            font-medium
            text-gray-900
            dark:text-gray-300
            ${isRequired && !React.isValidElement(inputLabel) ? 'after:content-["*"] after:ml-0.5 after:text-red-500' : ''}
          `}
        >
          {renderLabel()}
        </label>
      )}
      
      <div className="relative">
        <select
          id={selectId}
          name={name}
          value={value}
          onChange={(e) => onChange && onChange(e.target.value)}
          disabled={disabled}
          className={selectClassName}
          aria-label={typeof inputLabel === 'string' ? inputLabel : ''}
          aria-invalid={!!fieldError || !!errorMessage}
          aria-describedby={fieldError || errorMessage ? `${selectId}-error` : undefined}
          {...extraProps}
        >
          <option value="" disabled>
            {t(placeholder) || placeholder}
          </option>
          {options.map((option, index) => (
            <option
              key={option.value || `option-${index}`}
              value={option.value}
              disabled={option.disabled}
              className={option.disabled ? 'text-gray-400' : ''}
            >
              {t(option.label) || option.label}
            </option>
          ))}
        </select>
        
        {/* Custom dropdown arrow */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
          <svg
            className="fill-current h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
          >
            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
          </svg>
        </div>
        
        {/* Clear button */}
        {showClear && value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-6 flex items-center px-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title={t('CLEAR') || 'Clear'}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
      
      {/* Error message */}
      {(fieldError || errorMessage) && (
        <p
          id={`${selectId}-error`}
          className="mt-1 text-sm text-red-600 dark:text-red-400"
        >
          {fieldError?.message || errorMessage}
        </p>
      )}
    </div>
  );
};

export default OSelect;